import { useToastContext } from '@ark-ui/react/toast';
import {
  Box,
  HStack,
  Portal,
  Spinner,
  Stack,
  Toast,
  Toaster as ChakraToaster,
  createToaster,
  useToastStyles,
} from '@chakra-ui/react';

import type { ReactElement } from 'react';
import { ErrorIcon, SuccessIcon, WarningIcon, InfoIcon, CloseIcon } from 'assets/icons/v2';
import { NovaIcon } from './novaIcon';

export const toaster = createToaster({
  placement: 'top',
  pauseOnPageIdle: true,
});

const ToastLoadingIndicator = (): ReactElement => {
  const styles = useToastStyles();
  return (
    <Box as="span" css={styles.indicator}>
      <Spinner size="sm" color="text.primary" />
    </Box>
  );
};

const ToastStatusIcon = (): ReactElement => {
  const { type } = useToastContext();
  const styles = useToastStyles();

  const icon = (() => {
    switch (type) {
      case 'error':
        return <NovaIcon icon={ErrorIcon} size="l" color="icon.error" />;
      case 'warning':
        return <NovaIcon icon={WarningIcon} size="l" color="icon.warning" />;
      case 'success':
        return <NovaIcon icon={SuccessIcon} size="l" color="icon.accent" />;
      case 'info':
        return <NovaIcon icon={InfoIcon} size="l" color="icon.info" />;
      default:
        return <NovaIcon icon={ErrorIcon} size="l" color="icon.primary" />;
    }
  })();

  return (
    <Box as="span" css={styles.indicator}>
      {icon}
    </Box>
  );
};

export const Toaster = (): ReactElement => {
  return (
    <Portal>
      <ChakraToaster toaster={toaster} insetInline={{ mdDown: '4' }}>
        {(toast) => (
          <Toast.Root>
            {toast.type === 'loading' ? <ToastLoadingIndicator /> : <ToastStatusIcon />}
            <Stack gap="2xs" flex="1" alignItems="flex-start" minW={0} maxW="100%">
              {toast.title && <Toast.Title>{toast.title}</Toast.Title>}
              {toast.description && <Toast.Description>{toast.description}</Toast.Description>}
            </Stack>
            <HStack gap="xs" alignSelf="stretch" alignItems="flex-start">
              {toast.action && (
                <Toast.ActionTrigger
                  onClick={() => {
                    toast.action?.onClick?.();
                    toaster.dismiss(toast.id);
                  }}
                >
                  {toast.action.label}
                </Toast.ActionTrigger>
              )}
              {toast.closable && (
                <Toast.CloseTrigger aria-label="Toast schließen" cursor="pointer">
                  <NovaIcon icon={CloseIcon} size="m" />
                </Toast.CloseTrigger>
              )}
            </HStack>
          </Toast.Root>
        )}
      </ChakraToaster>
    </Portal>
  );
};
